/**
 * The first how-to: how to frame a shed floor, and skids vs. piers vs. a slab.
 *
 * A how-to teaches one technique for minutes rather than selling the plans in thirty
 * seconds, so it has its own shape: a cold open that shows the problem big and funny, a
 * title, and then chapters, each under a heading that lands in the middle of the stage
 * and rises to its top, with the explanation on a card of paper at the bottom. There is
 * no narrator — captions only — so every line holds long enough to read twice.
 *
 * What it teaches is what the build guide's floor steps say (`engine/src/guide.cpp`:
 * "Set the runners", "Set out the piers", "Build the beams", "Form and pour the slab",
 * "Bolt down the sills", "Frame the floor", "Lay the deck"), in the guide's order, and the
 * glossary's floor terms (`reference/glossary.ts`). The only figures in it are the trade's
 * rules of thumb — sixteen or twenty-four inches on center, three-four-five — never a
 * size, a span, a count or a length from a plan: those are what the plans are for, and
 * the film says so.
 *
 * Ten scenes on one clock (`AT`), each drawn in its own seconds, with its moments in
 * an exported table the score reads (`floorHowToScore.ts`).
 */

import { drawDog } from "./dog.js";
import type { Beat, Film, Frame, Site, View } from "./film.js";
import {
  BLOCK,
  D,
  GRAVEL,
  JOIST,
  LINES,
  NO_FRAME,
  NO_SLAB,
  ON_CENTER,
  SKID,
  SLAB,
  W,
  arrive,
  at,
  baseOf,
  beams,
  blockAt,
  blockTop,
  frame,
  frameCorners,
  gravelBed,
  ground,
  joistLines,
  piers,
  skidCorners,
  skids,
  slab,
  strings,
  type FrameWork,
  type SlabWork,
} from "./floorKit.js";
import { arrowDown, cross, heading, label, level, rain, say, tape, tick, type Chapter, type Line } from "./howToProps.js";
import type { Ink, Pt } from "./ink.js";
import { noise } from "./ink.js";
import { caption, dust, fence, hammerInHand, impactStar, landscape, shoot, speedLines, titleBlock, tree, wipe, type Camera } from "./props.js";
import { CHEER, CROUCH, KNEEL, STAND, THUMBS_UP, blend, drawFigure, hammer, idle, impact, walk, type Figure, type Pose } from "./rig.js";
import { BUILT, FLOOR, TEN_BY_TWELVE, drawShed, project, type Projection } from "./shed.js";
import { clamp, ease, mix, pulse, span } from "./timing.js";
import { pourStream, screed, wheelbarrow, wrenchInHand } from "./workshopProps.js";

/** When each scene starts, in the film's seconds. */
export const AT = {
  open: 0,
  title: 15,
  job: 21,
  three: 50,
  skids: 72,
  piers: 122,
  slab: 183,
  frame: 237,
  deck: 308,
  choose: 334,
  card: 361,
  end: 367,
};

/** The chapters, as the headings and the page's list (and a video's description) name them. */
export const CHAPTERS: readonly Chapter[] = [
  { at: AT.job, number: 1, title: "What the floor does" },
  { at: AT.three, number: 2, title: "Three ways to stand it" },
  { at: AT.skids, number: 3, title: "Skids" },
  { at: AT.piers, number: 4, title: "Piers" },
  { at: AT.slab, number: 5, title: "A slab" },
  { at: AT.frame, number: 6, title: "Frame the floor" },
  { at: AT.deck, number: 7, title: "Lay the deck" },
  { at: AT.choose, number: 8, title: "Which one?" },
];

/** When each chapter's heading leaves: where the next scene begins. */
function chapterEnd(chapter: Chapter): number {
  const next = CHAPTERS.find((each) => each.at > chapter.at);
  return next?.at ?? AT.card;
}

// ---- the scenes' moments, each in its own scene's seconds ------------------------------------

export const OPEN = {
  rain: [2.2, 7.0] as const,
  sink: [4.4, 6.4] as const,
  door: [6.6, 7.3] as const,
  /** The ball, from the doorway to off the edge of the frame. */
  ball: [7.1, 9.6] as const,
  /** Where it drops off the deck onto the ground, as a fraction of `ball`. */
  ballDrop: 0.16,
  chase: [8.2, 10.8] as const,
  palm: [9.8, 12.6] as const,
  zoom: [12.8, 15] as const,
};

export const TITLE = {
  piers: [0.3, 1.5] as const,
  beams: [1.3, 2.2] as const,
  rims: [2.1, 2.6] as const,
  joists: [2.4, 3.6] as const,
};

export const JOB = {
  /** The load, from the roof down to the ground: when each arrow starts to grow. */
  arrows: [3.0, 4.0, 5.0, 6.0] as const,
  part: 9.8,
  level: [11.0, 14.0] as const,
  levelTick: 14.3,
  diagonals: [14.6, 16.4] as const,
  diagonalsTick: 16.9,
  rain: [18.4, 27.6] as const,
  air: [20.0, 28.0] as const,
};

export const THREE = {
  gravel: [2.6, 3.4] as const,
  skids: [3.0, 4.0] as const,
  piers: [3.9, 4.9] as const,
  beams: [4.8, 5.6] as const,
  forms: [5.5, 6.3] as const,
  pour: [6.2, 7.2] as const,
  strike: [7.2, 7.8] as const,
  names: [3.6, 5.4, 7.4] as const,
  frames: [10.3, 12.6] as const,
  sills: [10.8, 12.6] as const,
  kinds: 12.8,
};

export const SKIDS = {
  rake: [3.0, 8.6] as const,
  gravel: [10.4, 13.8] as const,
  tamp: [14.0, 17.2] as const,
  /** Thumps of the tamper a second. */
  tampRate: 2.5,
  skids: [18.0, 21.0] as const,
  labels: [21.4, 22.4] as const,
  tapeA: [26.0, 27.2] as const,
  tapeB: [27.4, 28.6] as const,
  wrong: 29.2,
  shove: [30.4, 33.0] as const,
  right: 33.6,
  /** The back skid, out of square by this much along its length until it is shoved. */
  shift: 0.9,
  pull: [36.6, 38.4] as const,
  drag: [38.4, 40.2] as const,
  tumble: [40.2, 41.0] as const,
  slide: 0.7,
};

export const PIERS = {
  strings: [2.8, 7.4] as const,
  dig: [10.4, 18.2] as const,
  set: [22.0, 25.0] as const,
  board: [25.4, 26.0] as const,
  /** The mallet's three blows on the block that stands proud. */
  taps: [28.4, 29.0, 29.6] as const,
  levelTick: 30.4,
  beams: [34.0, 37.4] as const,
  joints: [39.4, 41.2] as const,
  air: [45.0, 51.4] as const,
  spacing: [52.4, 54.0] as const,
};

export const SLABS = {
  forms: [2.8, 6.0] as const,
  gravel: [9.0, 11.0] as const,
  barrier: [11.4, 13.4] as const,
  mesh: [13.8, 16.4] as const,
  pour: [19.0, 23.6] as const,
  strike: [23.8, 26.8] as const,
  bolts: [28.0, 31.0] as const,
  dog: [30.6, 32.6] as const,
  /** The dog, scooped up an inch from the wet concrete, and put down well clear of it. */
  scoop: [32.6, 36.4] as const,
  sealer: [38.0, 39.6] as const,
  sills: [39.8, 42.6] as const,
};

export const FRAMING = {
  rims: [3.0, 5.0] as const,
  marks: [10.4, 15.0] as const,
  onCenter: [16.4, 17.6] as const,
  /** In close on one joist, and out again. */
  crown: [22.3, 37.0] as const,
  flip: [29.4, 31.2] as const,
  joists: [38.0, 45.0] as const,
  /** Blows of the hammer a second while the joists go in. */
  rate: 2.4,
  square: [50.4, 56.4] as const,
  diagonals: [58.4, 60.8] as const,
  diagonalsTick: 61.4,
  span: [64.4, 65.8] as const,
};

export const DECKING = {
  deck: [3.0, 9.4] as const,
  joints: [12.0, 14.0] as const,
  diagonals: [20.0, 22.2] as const,
  diagonalsTick: 22.8,
};

export const CHOOSE = {
  words: [2.6, 4.2, 5.8] as const,
  part: 13.0,
  trot: [13.4, 15.4] as const,
  drop: 15.6,
  thumbs: 16.4,
};

/** The end card's rise, in the film's seconds. */
export const CARD = [AT.card + 0.2, AT.card + 0.85] as const;

/** Where a sheet of paper is pulled across the frame: between scenes, and within two of them. */
export const WIPES: readonly number[] = [
  AT.title,
  AT.job,
  AT.job + JOB.part,
  AT.three,
  AT.skids,
  AT.piers,
  AT.slab,
  AT.frame,
  AT.frame + FRAMING.crown[0],
  AT.frame + FRAMING.crown[1],
  AT.deck,
  AT.choose,
  AT.choose + CHOOSE.part,
];

// ---- the words ------------------------------------------------------------------------------

/** The open's captions, at the top of the stage as a film's are. */
export const OPEN_CAPTIONS: readonly { text: string; start: number; end: number; size?: number }[] = [
  { text: "Built it straight on the dirt?", start: 0.5, end: 4.4, size: 56 },
  { text: "Every shed starts with its floor.", start: 9.6, end: 12.7, size: 52 },
];

/** Every line of explanation, in the film's seconds. */
export function lines(site: Site): Line[] {
  const at = (scene: number, list: readonly [number, number, string, string?][]): Line[] =>
    list.map(([start, end, text, accent]) => ({ text, start: scene + start, end: scene + end, ...(accent === undefined ? {} : { accent }) }));
  return [
    ...at(AT.job, [
      [2.6, 9.4, "A floor carries everything in the shed down to the ground."],
      [10.2, 17.8, "It has to be level and square, or nothing built on it will be."],
      [18.6, 27.6, "And it keeps the wood up off the wet ground, with air under it."],
    ]),
    ...at(AT.three, [
      [2.6, 9.6, "Three ways to stand a shed: on skids, on piers, or on a slab."],
      [10.2, 17.4, "Skids and piers carry a framed floor. A slab is the floor itself."],
      [17.8, 21.6, "Here's how each one goes in."],
    ]),
    ...at(AT.skids, [
      [2.5, 9.4, "Clear the ground and rake it level."],
      [10.0, 17.4, "Spread a bed of gravel, and tamp it down hard."],
      [17.8, 25.0, "Set pressure-treated skids on it, parallel."],
      [25.5, 35.5, "Measure both diagonals. Shift a skid until they match."],
      [36.0, 43.2, "Simple and quick, and a small shed on skids can even be dragged."],
      [43.8, 49.6, "Best on firm ground that's close to level."],
    ]),
    ...at(AT.piers, [
      [2.5, 9.4, "String out the lines of the floor."],
      [9.9, 20.6, "Dig each block down to firm ground."],
      [21.4, 33.2, "Set the blocks, then level them all into one plane."],
      [33.8, 44.2, "Build each beam from boards nailed face to face, joints staggered over the blocks."],
      [44.8, 51.2, "Good for uneven ground and bigger sheds, with air moving under the floor."],
      [51.8, 60.6, "The beam's span sets how far apart the blocks go. Your plan spaces them."],
    ]),
    ...at(AT.slab, [
      [2.5, 8.0, "Set the forms square, where the slab's edges go."],
      [8.5, 17.8, "Inside them: gravel, a vapor barrier, and mesh."],
      [18.4, 27.2, "Pour the concrete, and strike it off level."],
      [27.6, 37.0, "Set the anchor bolts while it's still wet."],
      [37.5, 44.8, "Then bolt pressure-treated sills down on sill sealer."],
      [45.3, 53.4, "No frame to build: the slab is the floor. Strong and permanent, but the most work."],
    ]),
    ...at(AT.frame, [
      [2.5, 9.4, "Set the rim joists along the front and the back."],
      [9.9, 15.6, "Mark the joist layout on both rims, side by side."],
      [16.0, 21.8, "Usually 16 or 24 inches on center. Your plan says which."],
      [22.8, 29.4, "Sight down each joist. Most have a slight bow: the crown."],
      [29.8, 36.6, "Turn every crown up, so the load pulls it flat."],
      [37.6, 49.0, "Set each joist on its mark, and nail through the rim into its end."],
      [49.8, 57.6, "Check for square: 3 feet along one side, 4 along the other. The diagonal is 5."],
      [58.0, 63.6, "Or measure both diagonals. When they match, it's square."],
      [64.0, 70.4, "How deep the joists are comes from the span. Your plan sizes them."],
    ]),
    ...at(AT.deck, [
      [2.5, 10.8, "Glue and screw the sheets down, the long edge across the joists."],
      [11.3, 19.2, "Stagger the courses, so every end joint lands on a joist and no two line up."],
      [19.6, 25.6, "Check the diagonals one last time. Every wall is built on this."],
    ]),
    ...at(AT.choose, [
      [2.5, 12.6, "Which one? It depends on your ground, your shed, and what goes in it."],
      [13.6, 19.4, "Get the floor right, and everything above it goes up square."],
      [19.8, 26.4, `The plans size the joists and space the piers for your shed: ${site.price}`, site.price],
    ]),
  ];
}

function beats(site: Site): readonly Beat[] {
  return [
    {
      at: AT.open,
      caption: "Built it straight on the dirt?",
      action: "A shed set straight on the ground sinks at one corner in the rain, the door swings open, and the dog's ball rolls out down the tilted floor.",
    },
    { at: AT.title, action: "How to frame a shed floor, and skids vs. piers vs. a slab." },
    {
      at: AT.job,
      caption: "1. What the floor does.",
      action: "It carries the whole shed down to the ground, stays level and square for the walls, and keeps the wood up off the wet ground.",
    },
    {
      at: AT.three,
      caption: "2. Three ways to stand it.",
      action: "Skids, piers or a slab: skids and piers carry a framed floor, and a slab is the floor itself.",
    },
    {
      at: AT.skids,
      caption: "3. Skids.",
      action:
        "Clear and rake the ground, spread and tamp a bed of gravel, set treated skids on it parallel, and shift one until the " +
        "diagonals match. A small shed on skids can be dragged, and the dog tries.",
    },
    {
      at: AT.piers,
      caption: "4. Piers.",
      action:
        "String the lines, dig each block down to firm ground (the dog digs them all), level the blocks into one plane, and " +
        "build the beams with their joints staggered over the blocks.",
    },
    {
      at: AT.slab,
      caption: "5. A slab.",
      action:
        "Forms set square; gravel, a vapor barrier and mesh; the pour, struck off level; anchor bolts set wet (and the dog " +
        "caught just in time); then treated sills bolted down on sill sealer.",
    },
    {
      at: AT.frame,
      caption: "6. Frame the floor.",
      action:
        "Rim joists along the front and back, the layout marked on both, every joist crown up and nailed through the rim, and " +
        "the frame checked square by three-four-five and by its diagonals.",
    },
    {
      at: AT.deck,
      caption: "7. Lay the deck.",
      action: "Sheets glued and screwed across the joists, the courses staggered so every end joint lands on a joist, and the diagonals checked once more.",
    },
    {
      at: AT.choose,
      caption: "8. Which one?",
      action: "Skids for a small shed on firm, level ground; piers for uneven ground and bigger sheds; a slab for heavy loads and a shed that stays.",
    },
    {
      at: AT.choose + CHOOSE.part,
      caption: "Get the floor right.",
      action: `A shed on a proper floor, and the dog's ball stays where it is put. The plans size the joists and space the piers for your shed: ${site.price}.`,
    },
    { at: AT.card, action: `${site.brand}: build plans drawn to your dimensions, ${site.price}.` },
  ];
}

export const floorHowTo: Film = {
  duration: AT.end,
  title: "How to frame a shed floor: skids vs. piers vs. a slab",
  slug: "frame-a-shed-floor",
  summary:
    "A drawn how-to: what a shed floor has to do, how skids, piers and a concrete slab each carry it, and how to frame the " +
    "floor and lay its deck square.",
  lead: (site) =>
    "What a shed floor has to do, the three things it can stand on and how each goes in, then the frame and the deck, " +
    `checked square at every step. The plans size it all for your shed, for ${site.price}.`,
  beats,
  draw,
};

function draw(frame: Frame): void {
  const { t, ink, view, site } = frame;
  if (t < AT.title) {
    openScene(ink, view, t - AT.open);
  } else if (t < AT.job) {
    titleScene(ink, t - AT.title);
  } else if (t < AT.three) {
    jobScene(ink, view, t - AT.job);
  } else if (t < AT.skids) {
    threeScene(ink, view, t - AT.three, false);
  } else if (t < AT.piers) {
    skidsScene(ink, view, t - AT.skids);
  } else if (t < AT.slab) {
    piersScene(ink, view, t - AT.piers);
  } else if (t < AT.frame) {
    slabScene(ink, view, t - AT.slab);
  } else if (t < AT.deck) {
    frameScene(ink, view, t - AT.frame);
  } else if (t < AT.choose) {
    deckScene(ink, view, t - AT.deck);
  } else {
    chooseScene(ink, view, t - AT.choose);
  }

  for (const line of OPEN_CAPTIONS) {
    caption(ink, line.text, t, line.start, line.end, { size: line.size });
  }
  for (const chapter of CHAPTERS) {
    heading(ink, chapter, t, chapterEnd(chapter));
  }
  for (const line of lines(site)) {
    say(ink, line, t);
  }
  titleBlock(ink, span(t, ...CARD), site, { kind: "SHED PLANS", line1: "Floor to roof,", line2: "drawn to your size." });
  for (const at of WIPES) {
    wipe(ink, view, t, at);
  }
}

// ---- the furniture every lesson scene shares --------------------------------------------------

/** The floor's site: twelve feet across, in the middle of the stage. */
export const SITE: Projection = { x: -308, y: 150, scale: 40 };
/** The horizon behind the lesson scenes: high, so the ground under the floor reads as ground. */
const HORIZON = -60;

/** A point on the site, in feet, on the stage. */
function on(x: number, y: number, z: number, proj: Projection = SITE): Pt {
  return at(proj, x, y, z);
}

/** The backyard every lesson is drawn in: sky, grass, the fence and the tree, all past the floor. */
function backyard(ink: Ink, view: View, u: number): void {
  landscape(ink, view, HORIZON);
  fence(ink, view, HORIZON - 120, HORIZON);
  tree(ink, view.left + 110, HORIZON + 40, Math.sin(u * 1.2));
}

/** The builder, at a place on the site in feet. */
function builder(ink: Ink, pose: Pose, x: number, z: number, figure: Partial<Figure> = {}): ReturnType<typeof drawFigure> {
  const feet = on(x, 0, z);
  return drawFigure(ink, pose, { x: feet.x, ground: feet.y, facing: 1, scale: 1.3, eyes: "open", mouth: "smile", ...figure });
}

/** A card in the corner, for the answer to a check: "A ≠ B", then "A = B". */
function verdict(ink: Ink, x: number, y: number, text: string, good: boolean, k: number): void {
  if (k <= 0) {
    return;
  }
  const p = ink.palette;
  const { ctx } = ink;
  const pop = ease.back(clamp(k * 2));
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(pop, pop);
  ctx.rotate(-0.03);
  ink.rect(-96, -36, 192, 72, { fill: p.trim, width: 2.6, offset: 3 });
  ink.text(text, -18, 2, { size: 36, weight: 900 });
  if (good) {
    tick(ink, 58, 0, clamp(k * 2 - 0.6), 34);
  } else {
    cross(ink, 58, 0, clamp(k * 2 - 0.6), 28);
  }
  ctx.restore();
}

/** A breath of air moving under a floor: a short streak and its head. */
function airArrow(ink: Ink, from: Pt, alpha: number): void {
  if (alpha <= 0) {
    return;
  }
  const p = ink.palette;
  ink.line(from.x - 40, from.y, from.x + 10, from.y, { width: 3.4, color: p.trim, alpha, overshoot: false });
  ink.shape(
    [
      { x: from.x + 10, y: from.y - 8 },
      { x: from.x + 24, y: from.y },
      { x: from.x + 10, y: from.y + 8 },
    ],
    { fill: p.trim, fillAlpha: alpha, width: 1.6, alpha, offset: 0 },
  );
}

/** A label shown over a window of the scene, drawing on and off. */
function shown(u: number, from: number, until: number): number {
  return clamp(span(u, from, from + 0.6) - span(u, until, until + 0.4));
}

/** The dog's ball: a tennis ball, rolling. */
function ball(ink: Ink, x: number, y: number, spin: number, r = 9): void {
  const p = ink.palette;
  ink.circle(x, y - r, r, { fill: p.grass, width: 2, offset: 0.5 });
  ink.arc(x, y - r, r * 0.7, spin, spin + 2.2, { width: 1.6, color: p.trim });
}

// ---- the open: a shed on the dirt ------------------------------------------------------------

const OPEN_SHED: Projection = { x: -170, y: 236, scale: 22 };
const OPEN_GROUND = 236;

function openScene(ink: Ink, view: View, u: number): void {
  const p = ink.palette;
  const zoom = ease.in(span(u, ...OPEN.zoom));
  const corner = project(OPEN_SHED, 0, 0.4, 0);
  const camera: Camera = { x: mix(50, corner.x, zoom), y: mix(110, corner.y, zoom), zoom: mix(1.3, 4, zoom) };
  shoot(
    ink,
    camera,
    (v) => {
      landscape(ink, v, 70);
      fence(ink, v, -50, 70);
      tree(ink, -560, OPEN_GROUND - 6, Math.sin(u * 1.3));

      // The puddle, as the rain comes: under the low corner, where the shed sinks.
      const wet = span(u, OPEN.rain[0] + 0.6, OPEN.rain[1]);
      if (wet > 0) {
        ink.ellipse(corner.x + 10, OPEN_GROUND + 4, 120 * ease.out(wet), 18 * ease.out(wet), { fill: p.glass, fillAlpha: 0.7, width: 1.6, offset: 0 });
        ink.ellipse(corner.x + 170, OPEN_GROUND + 30, 60 * ease.out(wet), 9 * ease.out(wet), { fill: p.glass, fillAlpha: 0.6, width: 1.4, offset: 0 });
      }

      // The shed: on its deck, straight on the dirt; sinking at its front left corner.
      const sink = ease.inOut(span(u, ...OPEN.sink));
      const angle = -0.12 * sink;
      const pivot = project(OPEN_SHED, TEN_BY_TWELVE.width, 0, 0);
      const swing = ease.out(span(u, ...OPEN.door)) * 0.75;
      const { ctx } = ink;
      ctx.save();
      ctx.translate(pivot.x, pivot.y);
      ctx.rotate(angle);
      ctx.translate(-pivot.x, -pivot.y + 8 * sink);
      ctx.translate(0, (FLOOR - 0.1) * OPEN_SHED.scale);
      drawShed(ink, OPEN_SHED, TEN_BY_TWELVE, { ...BUILT, runners: 0, joists: 0, swing });
      ctx.restore();
      // A creak line or two where it gives.
      const give = pulse(u, OPEN.sink[0], OPEN.sink[0] + 0.8);
      if (give > 0) {
        speedLines(ink, corner.x - 26, corner.y - 30, give, 3, 40);
      }

      // Where a point drawn in the tilted shed ends up on the stage.
      const tilted = (x: number, y: number, z: number): Pt => {
        const raw = project(OPEN_SHED, x, y + FLOOR - 0.1, z);
        const local = { x: raw.x - pivot.x, y: raw.y + 8 * sink - pivot.y };
        return {
          x: pivot.x + local.x * Math.cos(angle) - local.y * Math.sin(angle),
          y: pivot.y + local.x * Math.sin(angle) + local.y * Math.cos(angle),
        };
      };

      // The ball, out of the doorway, off the deck and away down the slope.
      const rolled = span(u, ...OPEN.ball);
      if (rolled > 0 && rolled < 1) {
        const door = tilted(TEN_BY_TWELVE.width / 2, 0.05 - FLOOR + 0.1 + 0.1, 0.2);
        const dropped = span(rolled, 0, OPEN.ballDrop);
        const away = ease.in(span(rolled, OPEN.ballDrop, 1));
        const x = mix(door.x, door.x - 120, dropped) - away * 900;
        const y = rolled < OPEN.ballDrop ? mix(door.y, OPEN_GROUND + 14, ease.bounce(dropped)) : OPEN_GROUND + 14 - Math.abs(Math.sin(away * 9)) * 10 * (1 - away);
        ball(ink, x, y, -u * 14, 12);
      }

      // The dog: sitting by the door with nothing to do, then after the ball.
      const chase = span(u, ...OPEN.chase);
      if (chase <= 0) {
        drawDog(ink, { x: 120, ground: OPEN_GROUND + 22, facing: -1, mode: "sit", t: u, scale: 1.1 });
      } else {
        drawDog(ink, { x: mix(120, -900, ease.in(chase)), ground: OPEN_GROUND + 22, facing: -1, mode: "run", phase: u * 7, t: u, scale: 1.1 });
      }

      // The builder: proud of it, then not.
      const sank = u > OPEN.sink[0] + 0.3;
      const palm = span(u, ...OPEN.palm);
      const proud = u < OPEN.sink[0];
      const pose: Pose = proud ? THUMBS_UP : palm > 0 ? { ...STAND, nod: 0.25 } : blend(STAND, CHEER, 0.4 * pulse(u, OPEN.sink[0], OPEN.door[1]));
      const head = { x: 268, y: OPEN_GROUND - 196 };
      drawFigure(ink, pose, {
        x: 290,
        ground: OPEN_GROUND + 20,
        facing: -1,
        scale: 1.15,
        thumbA: proud,
        eyes: proud ? "happy" : palm > 0.2 ? "closed" : sank ? "wide" : "open",
        mouth: proud ? "grin" : sank ? "o" : "flat",
        look: 0.3,
        ...(palm > 0 ? { reachA: { x: mix(250, head.x + 2, ease.out(clamp(palm * 4))), y: mix(OPEN_GROUND - 80, head.y + 6, ease.out(clamp(palm * 4))) } } : {}),
      });

      rain(ink, v.left, v.right, v.top, OPEN_GROUND + 40, u, clamp(span(u, OPEN.rain[0], OPEN.rain[0] + 0.6) - span(u, OPEN.rain[1] - 0.6, OPEN.rain[1])));
    },
    view,
  );
}

// ---- the title ----------------------------------------------------------------------------------

const TITLE_SITE: Projection = { x: -200, y: 300, scale: 26 };

function titleScene(ink: Ink, u: number): void {
  const p = ink.palette;
  const proj = TITLE_SITE;
  piers(ink, proj, 1, span(u, ...TITLE.piers));
  beams(ink, proj, span(u, ...TITLE.beams));
  frame(ink, proj, baseOf("piers"), { ...NO_FRAME, rims: span(u, ...TITLE.rims), joists: span(u, ...TITLE.joists) });

  const k = (start: number): number => clamp(span(u, start, start + 0.25));
  const pop = (start: number): number => ease.back(span(u, start, start + 0.4));
  const { ctx } = ink;
  const line = (text: string, y: number, size: number, start: number, color = p.ink): void => {
    if (k(start) <= 0) {
      return;
    }
    ctx.save();
    ctx.translate(0, y);
    ctx.scale(mix(0.7, 1, pop(start)), mix(0.7, 1, pop(start)));
    ctx.rotate(-0.015);
    ink.text(text, 0, 0, { size, weight: 900, color, alpha: k(start) });
    ctx.restore();
  };
  ink.text("A HOW-TO", 0, -366, { size: 22, weight: 800, color: p.accent, tracking: 0.3, alpha: k(0.1) });
  line("How to frame", -290, 82, 0.2);
  line("a shed floor", -200, 82, 0.5);
  const width = ink.measure("a shed floor", { size: 82, weight: 900 });
  const drawn = ease.out(span(u, 0.8, 1.3));
  if (drawn > 0) {
    ink.curve(
      [
        { x: -width / 2 - 6, y: -150 },
        { x: 0, y: -158 },
        { x: width / 2 + 10, y: -151 },
      ],
      { width: 9, color: p.accent, draw: drawn },
    );
  }
  line("Skids vs. piers vs. a slab", -86, 44, 1.2, p.accent);
}

// ---- 1. what the floor does ----------------------------------------------------------------------

const JOB_SHED: Projection = { x: -190, y: 196, scale: 22 };

function jobScene(ink: Ink, view: View, u: number): void {
  if (u < JOB.part) {
    jobLoad(ink, view, u);
  } else {
    jobFloor(ink, view, u);
  }
}

/** The load, from the roof to the ground, arrow by arrow. */
function jobLoad(ink: Ink, view: View, u: number): void {
  landscape(ink, view, 120);
  const proj = JOB_SHED;
  const shed = TEN_BY_TWELVE;
  drawShed(ink, proj, shed, BUILT);
  const ground = proj.y;
  const [roof, walls, floor, soil] = JOB.arrows;
  const grow = (start: number): number => span(u, start, start + 0.7);
  // The roof's weight, on both slopes.
  for (const x of [1.5, 8.5]) {
    const top = project(proj, x, FLOOR + shed.wall + shed.rise * 0.9, 6);
    arrowDown(ink, top.x, top.y - 90, 70, grow(roof));
  }
  // Down the walls.
  for (const [x, z] of [
    [0, 0],
    [shed.width, 0],
  ] as const) {
    const top = project(proj, x, FLOOR + shed.wall * 0.8, z);
    arrowDown(ink, top.x + (x === 0 ? -26 : 26), top.y, 110, grow(walls));
  }
  // Into the floor and onto what it stands on.
  for (const x of [1.5, 5, 8.5]) {
    const top = project(proj, x, FLOOR, 0);
    arrowDown(ink, top.x, top.y + 6, 34, grow(floor));
  }
  // And spread into the ground.
  const spread = grow(soil);
  if (spread > 0) {
    for (let i = -3; i <= 3; i += 1) {
      const from = project(proj, 5 + i * 1.4, 0, 0);
      ink.line(from.x, ground + 12, from.x + i * 16 * ease.out(spread), ground + 12 + 46 * ease.out(spread), {
        width: 3,
        color: ink.palette.signal,
        alpha: 0.7,
        overshoot: false,
      });
    }
  }
  label(ink, "Roof", project(proj, 8.5, FLOOR + shed.wall + shed.rise * 0.6, 6), { x: 330, y: -200 }, span(u, roof + 0.3, roof + 0.9));
  label(ink, "Walls", project(proj, shed.width, FLOOR + shed.wall * 0.5, 4), { x: 340, y: -60 }, span(u, walls + 0.3, walls + 0.9));
  label(ink, "Floor", project(proj, shed.width, FLOOR * 0.6, 6), { x: 340, y: 70 }, span(u, floor + 0.3, floor + 0.9));
  label(ink, "Ground", { x: project(proj, 9, 0, 0).x, y: ground + 30 }, { x: 330, y: 200 }, span(u, soil + 0.3, soil + 0.9));
}

const JOB_SITE: Projection = { x: -308, y: 170, scale: 40 };

/** The floor on its own: level, square, and dry. */
function jobFloor(ink: Ink, view: View, u: number): void {
  const p = ink.palette;
  backyard(ink, view, u);
  const proj = JOB_SITE;
  ground(ink, proj, 0);
  // Puddles, as the rain comes.
  const wet = span(u, JOB.rain[0] + 1, JOB.rain[1]);
  if (wet > 0) {
    for (const [x, z, r] of [
      [-1.0, -1.0, 70],
      [W + 0.6, 2.0, 50],
      [5.5, D + 0.9, 60],
    ] as const) {
      const c = at(proj, x, 0, z);
      ink.ellipse(c.x, c.y, r * ease.out(wet), r * 0.3 * ease.out(wet), { fill: p.glass, fillAlpha: 0.7, width: 1.4, offset: 0 });
    }
  }
  piers(ink, proj, 1, 1);
  // Air under it, between the blocks and under the beams: drawn before them, so they cover it.
  const air = span(u, ...JOB.air);
  if (air > 0) {
    for (let i = 0; i < 6; i += 1) {
      const travel = ((u - JOB.air[0]) * 0.18 + i / 6) % 1;
      const z = [-0.4, 2.2, 5.8, -0.4, 2.2, 5.8][i]!;
      const from = at(proj, mix(-2, W + 2, (travel + (i >= 3 ? 0.5 : 0)) % 1), BLOCK * 0.45, z);
      const alpha = clamp(Math.sin(travel * Math.PI) * 2) * clamp(air * 4) * clamp((1 - air) * 8);
      airArrow(ink, from, alpha);
    }
  }
  beams(ink, proj, 1);
  const base = baseOf("piers");
  frame(ink, proj, base, { ...NO_FRAME, rims: 1, joists: 1, deck: 1 });
  label(ink, "Air", at(proj, W + 0.6, BLOCK * 0.45, -0.4), { x: 380, y: 240 }, shown(u, JOB.air[0] + 1, JOB.air[1] - 0.8));

  // Level: the bubble settles into the middle.
  const tools = shown(u, JOB.level[0], JOB.rain[0] - 0.4);
  const levelK = span(u, ...JOB.level);
  if (levelK > 0 && tools > 0.5) {
    const a = on(3.2, base + JOIST + 0.09, 3, proj);
    const b = on(8.8, base + JOIST + 0.09, 3, proj);
    const angle = Math.atan2(b.y - a.y, b.x - a.x);
    const settle = Math.cos(levelK * 9) * (1 - ease.out(levelK)) * 0.9;
    level(ink, a, Math.hypot(b.x - a.x, b.y - a.y), angle, settle);
    tick(ink, b.x + 40, b.y - 30, span(u, JOB.levelTick, JOB.levelTick + 0.4) * tools);
  }
  // Square: the two diagonals agree.
  const [fl, fr, bl, br] = frameCorners(proj, base + 0.09);
  const diag = span(u, ...JOB.diagonals);
  if (u < JOB.rain[0] - 0.4) {
    tape(ink, fl, br, span(diag, 0, 0.55), "A");
    tape(ink, fr, bl, span(diag, 0.45, 1), "B");
  }
  verdict(ink, 330, -220, "A = B", true, shown(u, JOB.diagonalsTick, JOB.rain[0] - 0.8));
  rain(ink, view.left, view.right, view.top, proj.y + 60, u, clamp(span(u, JOB.rain[0], JOB.rain[0] + 0.6) - span(u, JOB.rain[1] - 0.8, JOB.rain[1])));
}

// ---- 2. three ways to stand it, and 8. which one -----------------------------------------------

const MINI = 17;
const COLUMNS = [-300, 0, 300] as const;
const MINI_Y = 110;

function mini(column: number): Projection {
  return { x: COLUMNS[column]! - ((W + D * 0.42) * MINI) / 2, y: MINI_Y, scale: MINI };
}

/** The three foundations side by side; `done` draws them finished, for the last chapter. */
function threeScene(ink: Ink, view: View, u: number, done: boolean): void {
  const p = ink.palette;
  backyard(ink, view, u);
  const s = (stage: readonly [number, number]): number => (done ? 1 : span(u, ...stage));
  for (let column = 0; column < 3; column += 1) {
    ground(ink, mini(column), 0);
  }
  // Skids.
  gravelBed(ink, mini(0), s(THREE.gravel));
  skids(ink, mini(0), s(THREE.skids));
  // Piers.
  piers(ink, mini(1), 1, s(THREE.piers));
  beams(ink, mini(1), s(THREE.beams));
  // A slab.
  slab(ink, mini(2), { ...NO_SLAB, forms: s(THREE.forms), pour: s(THREE.pour), strike: s(THREE.strike), bolts: s(THREE.sills), sills: s(THREE.sills) });
  // The framed floors, over the skids and the piers.
  const frames = s(THREE.frames);
  const framed = (k: number): FrameWork => ({ ...NO_FRAME, rims: span(k, 0, 0.3), joists: span(k, 0.2, 0.7), deck: span(k, 0.6, 1) });
  frame(ink, mini(0), baseOf("skids"), framed(frames));
  frame(ink, mini(1), baseOf("piers"), framed(frames));

  const names = ["Skids", "Piers", "Slab"];
  const kinds = ["a framed floor", "a framed floor", "the floor itself"];
  names.forEach((name, column) => {
    const k = done ? 1 : span(u, THREE.names[column]!, THREE.names[column]! + 0.4);
    if (k <= 0) {
      return;
    }
    const x = COLUMNS[column]!;
    const { ctx } = ink;
    ctx.save();
    ctx.translate(x, 172);
    const pop = ease.back(k);
    ctx.scale(pop, pop);
    ink.text(name, 0, 0, { size: 44, weight: 900 });
    ctx.restore();
    const under = done ? 0 : span(u, THREE.kinds + column * 0.3, THREE.kinds + column * 0.3 + 0.4);
    if (under > 0) {
      ink.text(kinds[column]!, x, 214, { size: 24, weight: 700, color: p.accent, alpha: under });
    }
  });
}

/** The last chapter: the three again, with who each is for, then the payoff in the yard. */
function chooseScene(ink: Ink, view: View, u: number): void {
  if (u < CHOOSE.part) {
    threeScene(ink, view, u, true);
    const p = ink.palette;
    const words = [
      ["Small shed", "Firm, level ground", "Might move one day"],
      ["Uneven ground", "A bigger shed", "Air underneath"],
      ["Heavy loads", "A workshop", "Here to stay"],
    ];
    words.forEach((rows, column) => {
      rows.forEach((row, i) => {
        const k = span(u, CHOOSE.words[column]! + i * 0.25, CHOOSE.words[column]! + i * 0.25 + 0.3);
        if (k <= 0) {
          return;
        }
        ink.text(row, COLUMNS[column]!, 212 + i * 28, { size: 24, weight: 700, alpha: k, color: i === 0 ? p.accent : p.ink });
      });
    });
    return;
  }
  payoff(ink, view, u);
}

const PAYOFF_SHED: Projection = { x: -190, y: 230, scale: 22 };

/** A shed on a proper floor: the ball the dog drops in the doorway stays put. In the last chapter's seconds. */
function payoff(ink: Ink, view: View, u: number): void {
  const card = ease.inOut(span(u + AT.choose, CARD[0], CARD[1]));
  const camera: Camera = { x: mix(0, 10, card), y: mix(0, 130, card), zoom: mix(1, 0.9, card) };
  shoot(
    ink,
    camera,
    (v) => {
      landscape(ink, v, 70);
      fence(ink, v, -50, 70);
      tree(ink, -560, PAYOFF_SHED.y - 6, Math.sin(u * 1.3));
      drawShed(ink, PAYOFF_SHED, TEN_BY_TWELVE, { ...BUILT, swing: 0.75 });
      const doorway = project(PAYOFF_SHED, TEN_BY_TWELVE.width / 2 + 0.2, FLOOR, 0.3);
      const trot = span(u, ...CHOOSE.trot);
      const dropped = u >= CHOOSE.drop;
      const dogX = mix(-620, doorway.x - 70, ease.out(trot));
      const dogGround = PAYOFF_SHED.y + 22;
      if (trot < 1) {
        drawDog(ink, { x: dogX, ground: dogGround, facing: 1, mode: "walk", phase: u * 4, t: u, scale: 1.1 });
        ball(ink, dogX + 38, dogGround - 30, 0, 10);
      } else {
        drawDog(ink, { x: dogX, ground: dogGround, facing: 1, mode: "sit", t: u * 3, scale: 1.1 });
      }
      if (trot >= 1) {
        // From the dog's mouth onto the deck in the doorway, a bounce, and still.
        const fall = span(u, CHOOSE.drop - 0.2, CHOOSE.drop + 0.4);
        const from = { x: dogX + 38, y: dogGround - 30 };
        ball(ink, mix(from.x, doorway.x, fall), mix(from.y, doorway.y, ease.bounce(fall)), 0, 10);
        if (dropped) {
          tick(ink, doorway.x + 40, doorway.y - 50, span(u, CHOOSE.drop + 0.8, CHOOSE.drop + 1.2));
        }
      }
      const thumbs = u >= CHOOSE.thumbs;
      drawFigure(ink, thumbs ? THUMBS_UP : idle(u), {
        x: 300,
        ground: PAYOFF_SHED.y + 20,
        facing: -1,
        scale: 1.15,
        thumbA: thumbs,
        eyes: thumbs ? "happy" : "open",
        mouth: thumbs ? "grin" : "smile",
        look: 0.3,
      });
    },
    view,
  );
}

// ---- 3. skids ------------------------------------------------------------------------------------

/** When each thump of the tamper lands, in the skids scene's seconds. */
export function tampThumps(): number[] {
  const [start, stop] = SKIDS.tamp;
  const out: number[] = [];
  for (let n = 1; start + n / SKIDS.tampRate <= stop + 1e-9; n += 1) {
    out.push(start + n / SKIDS.tampRate);
  }
  return out;
}

/** How fast the rake goes back and forth, in radians a second of its swing. */
const RAKE_RATE = 7;

/** When each stroke of the rake turns, in the skids scene's seconds: where it scrapes. */
export function rakeStrokes(): number[] {
  const [start, stop] = SKIDS.rake;
  const out: number[] = [];
  // The head is at `sin(u * RAKE_RATE)`, so it turns where the cosine is zero.
  for (let n = Math.ceil((start * RAKE_RATE) / Math.PI - 0.5); (Math.PI * (n + 0.5)) / RAKE_RATE < stop; n += 1) {
    out.push((Math.PI * (n + 0.5)) / RAKE_RATE);
  }
  return out;
}

/** How high the tamper's plate is off the gravel, 0–1: it lands on every thump. */
function tamperLift(u: number): number {
  const [start, stop] = SKIDS.tamp;
  if (u <= start || u >= stop) {
    return 0;
  }
  const phase = ((u - start) * SKIDS.tampRate) % 1;
  return phase < 0.75 ? ease.out(phase / 0.75) : 1 - ease.in((phase - 0.75) / 0.25);
}

function skidsScene(ink: Ink, view: View, u: number): void {
  const p = ink.palette;
  backyard(ink, view, u);
  // The whole floor slides as the dog drags it.
  const slide = ease.inOut(span(u, ...SKIDS.drag)) * SKIDS.slide;
  const proj: Projection = { ...SITE, x: SITE.x - slide * SITE.scale };
  ground(ink, SITE, 1 - ease.inOut(span(u, ...SKIDS.rake)));
  gravelBed(ink, SITE, span(u, ...SKIDS.gravel));
  const shift = SKIDS.shift * (1 - ease.inOut(span(u, ...SKIDS.shove)));
  skids(ink, proj, span(u, ...SKIDS.skids), shift);

  label(ink, "Skid", on(W + 0.3, GRAVEL + SKID, LINES[0]!, proj), { x: 360, y: 210 }, shown(u, SKIDS.labels[0], 25.4));
  label(ink, "Pressure treated", on(W * 0.6, GRAVEL + SKID, LINES[1]!, proj), { x: 250, y: -210 }, shown(u, SKIDS.labels[1], 25.4));

  // The diagonals, before the shove and after it.
  const [fl, fr, bl, br] = skidCorners(proj, shift);
  const measuring = u >= SKIDS.tapeA[0] && u < 35.6;
  if (measuring) {
    tape(ink, fl, br, span(u, ...SKIDS.tapeA), "A");
    tape(ink, fr, bl, span(u, ...SKIDS.tapeB), "B");
  }
  verdict(ink, 330, -220, "A ≠ B", false, shown(u, SKIDS.wrong, SKIDS.shove[0] + 0.6));
  verdict(ink, 330, -220, "A = B", true, shown(u, SKIDS.right, 35.6));

  // The builder: raking, barrowing the gravel, tamping it, watching, and shoving.
  if (u < SKIDS.rake[1] + 0.4) {
    const k = span(u, ...SKIDS.rake);
    const x = mix(-1.6, W + 0.6, k);
    const feet = on(x, 0, -1.2);
    const head = { x: feet.x + 92 + Math.sin(u * RAKE_RATE) * 18, y: feet.y + 2 };
    const top = { x: feet.x + 6, y: feet.y - 150 };
    const grip = (m: number): Pt => ({ x: mix(top.x, head.x, m), y: mix(top.y, head.y, m) });
    drawFigure(ink, { ...walk(u * 2.2, 0.6), lean: 0.2 }, { x: feet.x, ground: feet.y, facing: 1, scale: 1.3, reachA: grip(0.32), reachB: grip(0.12), mouth: "flat" });
    ink.line(top.x, top.y, head.x, head.y, { width: 4, color: p.woodDark, overshoot: false });
    ink.line(head.x - 4, head.y - 20, head.x + 4, head.y + 4, { width: 5, overshoot: false });
    dust(ink, head.x, head.y, (u * 2) % 1, 0.7);
  } else if (u < SKIDS.gravel[1] + 0.3) {
    const k = span(u, ...SKIDS.gravel);
    const x = mix(-0.5, W - 1.5, k);
    const wheel = on(x, 0, -1.3);
    const grip = wheelbarrow(ink, wheel.x, wheel.y, 0.7 + 0.15 * Math.sin(u * 3), 1 - k);
    pourStream(ink, { x: wheel.x + 4, y: wheel.y - 30 }, on(x + 1, GRAVEL, mix(D, -0.6, ease.inOut(k))), k > 0 && k < 1 ? 0.6 : 0);
    drawFigure(ink, { ...walk(u * 1.6, 0.4), lean: 0.3 }, { x: grip.x - 46, ground: wheel.y, facing: 1, scale: 1.3, reachA: grip, reachB: { x: grip.x - 2, y: grip.y + 4 }, mouth: "flat" });
  } else if (u < SKIDS.tamp[1] + 0.2) {
    const lift = tamperLift(u);
    const plate = on(5.5, GRAVEL + lift * 0.9, 0.3);
    const top = { x: plate.x - 6, y: plate.y - 140 };
    ink.line(plate.x, plate.y, top.x, top.y, { width: 5, color: p.woodDark, overshoot: false });
    ink.rect(top.x - 20, top.y - 3, 40, 7, { fill: p.woodDark, width: 2, offset: 0 });
    ink.rect(plate.x - 30, plate.y - 6, 60, 10, { fill: p.roof, width: 2.4, offset: 0 });
    const feet = on(4.2, 0, -1.4);
    drawFigure(ink, { ...STAND, lean: 0.12 }, { x: feet.x, ground: feet.y, facing: 1, scale: 1.3, reachA: { x: top.x - 10, y: top.y + 2 }, reachB: { x: top.x + 10, y: top.y + 2 }, mouth: "flat", eyes: lift < 0.1 ? "closed" : "open" });
    if (lift < 0.12 && u > SKIDS.tamp[0] + 0.1) {
      dust(ink, plate.x, plate.y + 4, 1 - lift * 8, 0.9);
    }
  } else if (u < SKIDS.shove[0]) {
    builder(ink, idle(u), -2.2, -1.2, { look: 0.6 });
  } else if (u < SKIDS.pull[0]) {
    // Shoving the back skid along its length until the diagonals agree.
    const end = on(W + 0.3 + shift, GRAVEL + SKID * 0.6, LINES[2]!, proj);
    const pushing = u < SKIDS.shove[1];
    drawFigure(ink, pushing ? { ...CROUCH, lean: 0.6 } : idle(u), {
      x: end.x + 62,
      ground: end.y + 30,
      facing: -1,
      scale: 1.3,
      reachA: pushing ? { x: end.x + 4, y: end.y } : undefined,
      reachB: pushing ? { x: end.x + 6, y: end.y + 6 } : undefined,
      mouth: pushing ? "flat" : "smile",
      eyes: pushing ? "closed" : "happy",
    });
  } else {
    builder(ink, u > SKIDS.tumble[0] ? CHEER : idle(u), W + 2.6, -1.6, { eyes: u > SKIDS.tumble[0] ? "happy" : "open", mouth: u > SKIDS.tumble[0] ? "grin" : "smile", look: -0.4, facing: -1 });
  }

  // The dog: a rope from the front skid, and a tug that moves a whole floor.
  if (u >= SKIDS.pull[0]) {
    const knot = on(-0.3, GRAVEL + SKID * 0.5, LINES[0]!, proj);
    const tumbled = u >= SKIDS.tumble[0];
    const strain = u < SKIDS.drag[0] ? Math.sin(u * 22) * 3 : 0;
    const dogX = mix(knot.x - 70, knot.x - 110, ease.out(span(u, SKIDS.pull[0], SKIDS.pull[0] + 0.5))) - slide * SITE.scale * 0.3 + strain;
    const dogGround = knot.y + 52;
    const mouth = { x: dogX - 30, y: dogGround - 38 };
    ink.curve(
      [knot, { x: mix(knot.x, mouth.x, 0.5), y: Math.max(knot.y, mouth.y) + (tumbled ? 20 : 4) }, mouth],
      { width: 2.6, color: p.woodDark },
    );
    if (tumbled) {
      const fall = span(u, ...SKIDS.tumble);
      drawDog(ink, { x: dogX - 30 * ease.out(fall), ground: dogGround, facing: -1, mode: fall < 1 ? "jump" : "sit", air: fall, t: u });
      if (fall >= 1) {
        impactStar(ink, dogX - 46, dogGround - 4, span(u, SKIDS.tumble[1], SKIDS.tumble[1] + 0.3), 0.7);
      }
    } else {
      drawDog(ink, { x: dogX, ground: dogGround, facing: -1, mode: "walk", phase: u * (u < SKIDS.drag[0] ? 9 : 4), t: u });
    }
    if (u > SKIDS.drag[0] && u < SKIDS.drag[1]) {
      speedLines(ink, on(W + 0.4, GRAVEL + SKID, LINES[1]!, proj).x + 30, on(W, GRAVEL + SKID, LINES[1]!, proj).y, pulse(u, ...SKIDS.drag), 4, 60);
      dust(ink, on(W, GRAVEL, 0, proj).x, on(W, GRAVEL, 0, proj).y, span(u, ...SKIDS.drag), 1);
    }
  } else if (u > 17.4) {
    drawDog(ink, { x: 390, ground: SITE.y + 80, facing: -1, mode: "sit", t: u });
  }
}

// ---- 4. piers ------------------------------------------------------------------------------------

/** The block the level finds high: the left end of the front line. */
const PROUD = 6;
/** How high it stands before the mallet, in feet. */
const PROUD_BY = 0.2;

/** How high the proud block still stands at `u`: a third lower at each blow. */
function proudBy(u: number): number {
  const blows = PIERS.taps.filter((tap) => u >= tap).length;
  return PROUD_BY * (1 - blows / PIERS.taps.length);
}

function piersScene(ink: Ink, view: View, u: number): void {
  const p = ink.palette;
  backyard(ink, view, u);
  const proj = SITE;
  ground(ink, proj, 0);
  strings(ink, proj, span(u, ...PIERS.strings) * (1 - span(u, 33.6, 34.0)));
  const dug = span(u, ...PIERS.dig);
  const proud = proudBy(u);
  piers(ink, proj, dug, span(u, ...PIERS.set), { index: PROUD, by: proud });
  // Air under the floor, between the blocks: drawn before the beams, so they cover it.
  const air = span(u, ...PIERS.air);
  if (air > 0 && air < 1) {
    for (let i = 0; i < 6; i += 1) {
      const travel = ((u - PIERS.air[0]) * 0.2 + i / 6) % 1;
      const from = at(proj, mix(-2, W + 2, travel), BLOCK * 0.45, [-0.4, 2.2, 5.8][i % 3]!);
      airArrow(ink, from, clamp(Math.sin(travel * Math.PI) * 2) * clamp(air * 6) * clamp((1 - air) * 6));
    }
  }
  beams(ink, proj, span(u, ...PIERS.beams), shown(u, PIERS.joints[0], 44.2));

  label(ink, "Pier block", blockTop(proj, 7), { x: 140, y: 236 }, shown(u, PIERS.set[1], 33.4));
  label(ink, "Floor beam", on(W + 0.3, BLOCK + 0.4, LINES[1]!, proj), { x: 360, y: -60 }, shown(u, PIERS.beams[1] + 0.4, 44.2));
  label(ink, "Joint over a block", on(W / 2, BLOCK + 0.6, LINES[0]! - 0.2, proj), { x: -200, y: 250 }, shown(u, PIERS.joints[0] + 0.4, 44.2));

  // The straight board and the level, across the front line of blocks.
  const boardK = shown(u, PIERS.board[0], 33.4);
  if (boardK > 0) {
    const left = blockTop(proj, PROUD, proud);
    const right = blockTop(proj, 8);
    // Resting on the high block at the left and the right one, clear of the middle block.
    const slope = (right.y - left.y) / (right.x - left.x);
    const drop = (1 - ease.out(boardK)) * 40;
    const a = { x: left.x - 24, y: left.y - 24 * slope - drop };
    const b = { x: right.x + 24, y: right.y + 24 * slope - drop };
    ink.shape(
      [
        { x: a.x, y: a.y - 10 },
        { x: b.x, y: b.y - 10 },
        { x: b.x, y: b.y },
        { x: a.x, y: a.y },
      ],
      { fill: p.wood, width: 2, offset: 0.6, overshoot: false },
    );
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    const from = 0.5 - 100 / length;
    level(ink, { x: mix(a.x, b.x, from), y: mix(a.y, b.y, from) - 10 }, 200, Math.atan(slope), -proud / PROUD_BY);
    tick(ink, right.x + 60, right.y - 70, span(u, PIERS.levelTick, PIERS.levelTick + 0.4) * boardK);
  }

  // The builder: watching the dog dig, dusted with what it throws, then the mallet.
  const digging = u >= PIERS.dig[0] - 0.4 && u < PIERS.dig[1] + 0.3;
  const dirt = span(u, PIERS.dig[0] + 1, PIERS.dig[1]);
  if (u < PIERS.set[0]) {
    const held = builder(ink, digging ? { ...STAND, lean: -0.08 } : idle(u), -2.6, -1.6, {
      eyes: digging ? "wide" : "open",
      mouth: digging ? "o" : "smile",
      look: 0.7,
      hold: (pen, hand) => {
        // A shovel, never used.
        pen.line(hand.x, hand.y - 40, hand.x, hand.y + 70, { width: 4, color: p.woodDark, overshoot: false });
        pen.shape(
          [
            { x: hand.x - 12, y: hand.y + 70 },
            { x: hand.x + 12, y: hand.y + 70 },
            { x: hand.x + 8, y: hand.y + 100 },
            { x: hand.x - 8, y: hand.y + 100 },
          ],
          { fill: p.roof, width: 2, offset: 0 },
        );
      },
    });
    if (dirt > 0) {
      dirty(ink, [held.head, held.shoulder, held.handA, held.pelvis], dirt);
    }
  } else if (u < PIERS.taps[2] + 1.2) {
    const phase = clamp((u - (PIERS.taps[0] - 0.6)) / 0.6, 0, 3);
    const block = blockTop(proj, PROUD, proud);
    const at0 = { x: block.x - 100, y: on(0, 0, -1.2).y };
    drawFigure(ink, u < PIERS.taps[0] - 0.6 ? KNEEL : hammer(phase), { x: at0.x, ground: at0.y, facing: 1, scale: 1.3, hold: hammerInHand, mouth: "flat" });
    impactStar(ink, block.x - 20, block.y - 14, impact(phase) * (phase > 0.5 ? 1 : 0));
  } else {
    builder(ink, idle(u), -2.4, -1.4, { look: 0.6 });
  }

  // The dog, digging every hole before the builder can start one.
  if (digging) {
    const n = 9;
    const hole = Math.min(n - 1, Math.floor(dug * (n + 1)));
    const { x, z } = blockAt(hole);
    const spot = on(x + 0.4, 0, z - 0.2);
    const hop = (u * 5) % 1;
    drawDog(ink, { x: spot.x + 26, ground: spot.y + 6, facing: -1, mode: "jump", air: hop * 0.35, t: u, scale: 0.9 });
    // Clods, over its shoulder toward the builder.
    const target = on(-2.6, 1.8, -1.6);
    for (let i = 0; i < 3; i += 1) {
      const k = ((u * 2.2 + i / 3) % 1);
      const cx = mix(spot.x, target.x + noise(Math.floor(u * 2.2 + i / 3), i) * 40, k);
      const cy = mix(spot.y, target.y, k) - Math.sin(k * Math.PI) * 120;
      ink.circle(cx, cy, 7, { fill: p.woodDark, width: 1.6, offset: 0 });
    }
  } else if (u >= PIERS.dig[1] + 0.3) {
    const sat = on(W + 2.2, 0, 0);
    drawDog(ink, { x: sat.x, ground: sat.y + 40, facing: -1, mode: "sit", t: u });
    if (u < PIERS.set[0] + 2) {
      dirty(ink, [{ x: sat.x - 20, y: sat.y + 10 }, { x: sat.x, y: sat.y + 20 }], 1);
    }
  }

  // How far apart the blocks are.
  const spacing = span(u, ...PIERS.spacing);
  if (spacing > 0 && u < 60.6) {
    tape(ink, on(blockAt(6).x, BLOCK + 0.1, LINES[0]! - 0.6), on(blockAt(7).x, BLOCK + 0.1, LINES[0]! - 0.6), spacing, "?");
  }
}

/** Specks of dirt on whoever was standing where the dog dug. */
function dirty(ink: Ink, points: readonly Pt[], k: number): void {
  const p = ink.palette;
  points.forEach((point, j) => {
    for (let i = 0; i < 6; i += 1) {
      ink.dot(point.x + noise(i, j * 13) * 14, point.y + noise(i, j * 13 + 1) * 10, 3, p.woodDark, k);
    }
  });
}

// ---- 5. a slab -----------------------------------------------------------------------------------

function slabWork(u: number): SlabWork {
  return {
    forms: span(u, ...SLABS.forms),
    gravel: span(u, ...SLABS.gravel),
    barrier: span(u, ...SLABS.barrier),
    mesh: span(u, ...SLABS.mesh),
    pour: span(u, ...SLABS.pour),
    strike: span(u, ...SLABS.strike),
    bolts: span(u, ...SLABS.bolts),
    sealer: span(u, ...SLABS.sealer),
    sills: span(u, ...SLABS.sills),
  };
}

function slabScene(ink: Ink, view: View, u: number): void {
  backyard(ink, view, u);
  const proj = SITE;
  ground(ink, proj, 0);
  const work = slabWork(u);
  slab(ink, proj, work);

  label(ink, "Gravel", on(1.5, 0.1, 1.2), { x: -330, y: 240 }, shown(u, SLABS.gravel[1] - 0.6, SLABS.barrier[0] + 0.6));
  label(ink, "Vapor barrier", on(7, 0.12, 2), { x: 0, y: 250 }, shown(u, SLABS.barrier[1] - 0.6, SLABS.mesh[0] + 0.8));
  label(ink, "Mesh", on(W - 1.5, 0.2, 2.5), { x: 330, y: 240 }, shown(u, SLABS.mesh[1] - 0.4, SLABS.pour[0]));
  label(ink, "Anchor bolt", on(W / 2, SLAB + 0.3, 0.25), { x: 120, y: 250 }, shown(u, SLABS.bolts[1], SLABS.sealer[0]));
  label(ink, "Sill plate", on(W, SLAB + 0.16, 3), { x: 360, y: 230 }, shown(u, SLABS.sills[1], 53.4));

  // The builder at each stage of it.
  if (u < SLABS.forms[1] + 0.4) {
    const k = span(u, ...SLABS.forms);
    const at0 = on(mix(-1.6, W + 0.4, k), 0, -1.6);
    const phase = u * 2.4;
    drawFigure(ink, hammer(phase), { x: at0.x, ground: at0.y, facing: 1, scale: 1.3, hold: hammerInHand, mouth: "flat" });
    impactStar(ink, at0.x + 75, at0.y - 12, impact(phase));
  } else if (u < SLABS.pour[0]) {
    builder(ink, idle(u), -2.4, -1.4, { look: 0.6 });
  } else if (u < SLABS.pour[1] + 0.2) {
    const k = span(u, ...SLABS.pour);
    const x = mix(0.5, W - 2, k);
    const wheel = on(x, 0, -1.3);
    const grip = wheelbarrow(ink, wheel.x, wheel.y, 0.75 + 0.15 * Math.sin(u * 3), 1 - k);
    pourStream(ink, { x: wheel.x + 4, y: wheel.y - 30 }, on(x + 1, SLAB, D * (1 - ease.inOut(k)) + 0.3), k);
    drawFigure(ink, { ...walk(u * 1.6, 0.4), lean: 0.3 }, { x: grip.x - 46, ground: wheel.y, facing: 1, scale: 1.3, reachA: grip, reachB: { x: grip.x - 2, y: grip.y + 4 }, mouth: "flat" });
  } else if (u < SLABS.strike[1] + 0.3) {
    const k = span(u, ...SLABS.strike);
    const z = D * k;
    const a = on(-0.3, SLAB + 0.3, z);
    const b = on(W + 0.3, SLAB + 0.3, z);
    const feet = on(-1.8, 0, z);
    drawFigure(ink, CROUCH, { x: feet.x, ground: feet.y, facing: 1, scale: 1.3, reachA: a, reachB: { x: a.x - 6, y: a.y + 2 }, mouth: "flat" });
    screed(ink, { x: a.x - 10, y: a.y }, b);
  } else if (u < SLABS.dog[1]) {
    const k = span(u, ...SLABS.bolts);
    const at0 = on(mix(0, W - 1, k), 0, -1.5);
    drawFigure(ink, { ...KNEEL, armA: 1.3 + Math.sin(u * 8) * 0.1, elbowA: 0.4 }, { x: at0.x, ground: at0.y, facing: 1, scale: 1.3, mouth: "flat" });
  } else if (u < SLABS.scoop[1]) {
    // The dog, caught an inch from the wet concrete, and carried off it.
    const scoop = span(u, ...SLABS.scoop);
    const lift = ease.out(clamp(scoop * 4));
    const carry = ease.inOut(span(scoop, 0.4, 1));
    const feet = { x: mix(on(W + 1.6, 0, -1.4).x, 420, carry), y: on(W, 0, -1.4).y };
    const dogAt = { x: feet.x + 34, y: feet.y - 30 - lift * 70 };
    drawFigure(ink, blend(STAND, CROUCH, (1 - lift) * 0.5), {
      x: feet.x,
      ground: feet.y,
      facing: 1,
      scale: 1.3,
      reachA: { x: dogAt.x - 6, y: dogAt.y - 14 },
      reachB: { x: dogAt.x + 14, y: dogAt.y - 10 },
      eyes: lift < 1 ? "wide" : "closed",
      mouth: lift < 1 ? "o" : "smile",
      look: 0.8,
    });
    drawDog(ink, { x: dogAt.x, ground: dogAt.y, facing: -1, mode: "walk", phase: u * 10, t: u, scale: 0.95 });
  } else if (u < SLABS.sills[1] + 0.4) {
    const k = span(u, SLABS.sealer[0], SLABS.sills[1]);
    const at0 = on(mix(0.5, W - 1, k), 0, -1.5);
    drawFigure(ink, { ...KNEEL, armA: 1.25 + Math.sin(u * 9) * 0.2, elbowA: 0.6 }, { x: at0.x, ground: at0.y, facing: 1, scale: 1.3, hold: wrenchInHand, mouth: "flat" });
  } else {
    builder(ink, idle(u), -2.4, -1.4, { look: 0.6 });
  }

  // The dog: on its way across, and then sitting well clear of it.
  if (u >= SLABS.dog[0] && u < SLABS.scoop[0]) {
    const k = span(u, ...SLABS.dog);
    const edge = on(W + 0.3, 0, -0.4);
    drawDog(ink, { x: mix(460, edge.x + 10, k), ground: edge.y + 30 - k * 10, facing: -1, mode: "walk", phase: u * 5, t: u, scale: 0.95 });
  } else if (u >= SLABS.scoop[1]) {
    drawDog(ink, { x: 430, ground: SITE.y + 90, facing: -1, mode: "sit", t: u, scale: 0.95 });
  }
}

// ---- 6. frame the floor, and 7. lay the deck ----------------------------------------------------

const SKID_BASE = baseOf("skids");

/** When each blow of the hammer lands while the joists go in, in the frame scene's seconds. */
export function joistBlows(): number[] {
  const [start, stop] = FRAMING.joists;
  const out: number[] = [];
  for (let n = 1; start + n / FRAMING.rate <= stop + 1e-9; n += 1) {
    out.push(start + n / FRAMING.rate);
  }
  return out;
}

function frameScene(ink: Ink, view: View, u: number): void {
  if (u >= FRAMING.crown[0] && u < FRAMING.crown[1]) {
    crownScene(ink, view, u - FRAMING.crown[0]);
    return;
  }
  // In close on the front left corner for three-four-five, and back out for the diagonals.
  const close = ease.inOut(span(u, FRAMING.square[0] - 0.6, FRAMING.square[0])) - ease.inOut(span(u, FRAMING.square[1] + 0.3, FRAMING.diagonals[0]));
  const corner = on(1.6, SKID_BASE + JOIST, 2);
  const camera: Camera = { x: mix(0, corner.x, close), y: mix(0, corner.y + 30, close), zoom: mix(1, 2, close) };
  shoot(ink, camera, (v) => framing(ink, v, u), view);
}

function framing(ink: Ink, view: View, u: number): void {
  const p = ink.palette;
  backyard(ink, view, u);
  const proj = SITE;
  ground(ink, proj, 0);
  gravelBed(ink, proj, 1);
  skids(ink, proj, 1);
  frame(ink, proj, SKID_BASE, {
    ...NO_FRAME,
    rims: span(u, ...FRAMING.rims),
    marks: span(u, ...FRAMING.marks),
    joists: span(u, ...FRAMING.joists),
  });
  const top = SKID_BASE + JOIST;
  label(ink, "Rim joist", on(W * 0.75, top - 0.2, 0), { x: 280, y: 250 }, shown(u, FRAMING.rims[1] + 0.4, 15.6));
  label(ink, "Rim joist", on(W * 0.25, top, D), { x: -200, y: -230 }, shown(u, FRAMING.rims[1] + 0.6, 15.6));
  // Sixteen inches, from one mark to the next.
  const oc = span(u, ...FRAMING.onCenter);
  if (oc > 0 && u < 22) {
    tape(ink, on(0, top + 0.05, -0.15), on(ON_CENTER, top + 0.05, -0.15), oc, "16");
  }
  label(ink, "Joist", on(joistLines()[4]! + 0.08, top, D / 2), { x: 120, y: -230 }, shown(u, FRAMING.joists[1], 49.4));

  // Three-four-five at the front left corner, then the diagonals.
  const square = span(u, ...FRAMING.square);
  if (square > 0 && u < 57.6) {
    const o = on(0, top, 0);
    const three = on(3, top, 0);
    const four = on(0, top, 4);
    tape(ink, o, three, span(square, 0, 0.25), "3", undefined, 0.7);
    tape(ink, o, four, span(square, 0.25, 0.5), "4", undefined, 0.75);
    tape(ink, three, four, span(square, 0.55, 0.85), "5", undefined, 0.5);
    tick(ink, mix(three.x, four.x, 0.5) + 40, mix(three.y, four.y, 0.5) - 30, span(square, 0.9, 1));
  }
  const [fl, fr, bl, br] = frameCorners(proj, SKID_BASE);
  const diag = span(u, ...FRAMING.diagonals);
  if (diag > 0 && u < 63.6) {
    tape(ink, fl, br, span(diag, 0, 0.5), "A");
    tape(ink, fr, bl, span(diag, 0.5, 1), "B");
  }
  verdict(ink, 330, -220, "A = B", true, shown(u, FRAMING.diagonalsTick, 63.6));

  // The span: front rim to back rim, the length a joist has to cross.
  const spanK = span(u, ...FRAMING.span);
  if (spanK > 0) {
    const x = joistLines()[6]! + 0.08;
    const a = on(x, top + 0.4, 0.2);
    const b = on(x, top + 0.4, D - 0.2);
    const end = { x: mix(a.x, b.x, ease.out(spanK)), y: mix(a.y, b.y, ease.out(spanK)) };
    ink.line(a.x, a.y, end.x, end.y, { width: 4, color: p.accent, overshoot: false });
    for (const [from, toward] of [
      [a, b],
      [end, a],
    ] as const) {
      const ang = Math.atan2(toward.y - from.y, toward.x - from.x);
      ink.line(from.x, from.y, from.x + Math.cos(ang + 0.5) * 16, from.y + Math.sin(ang + 0.5) * 16, { width: 4, color: p.accent, overshoot: false });
      ink.line(from.x, from.y, from.x + Math.cos(ang - 0.5) * 16, from.y + Math.sin(ang - 0.5) * 16, { width: 4, color: p.accent, overshoot: false });
    }
    label(ink, "Span", { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, { x: 330, y: -160 }, span(u, FRAMING.span[1], FRAMING.span[1] + 0.6));
  }

  // The builder: laying out the rims, then nailing the joists in from the front.
  if (u >= FRAMING.marks[0] && u < FRAMING.marks[1] + 0.2) {
    const k = span(u, ...FRAMING.marks);
    const at0 = on(mix(-0.4, W - 1.2, k), 0, -1.4);
    drawFigure(ink, { ...KNEEL, armA: 1.35, elbowA: 0.5 + Math.sin(u * 10) * 0.15 }, { x: at0.x, ground: at0.y, facing: 1, scale: 1.3, mouth: "flat" });
  } else if (u >= FRAMING.joists[0] && u < FRAMING.joists[1] + 0.3) {
    const k = span(u, ...FRAMING.joists);
    const phase = Math.min((u - FRAMING.joists[0]) * FRAMING.rate, (FRAMING.joists[1] - FRAMING.joists[0]) * FRAMING.rate);
    const at0 = on(mix(-1.4, W - 2, k), 0, -1.4);
    drawFigure(ink, hammer(phase), { x: at0.x, ground: at0.y, facing: 1, scale: 1.3, hold: hammerInHand, mouth: "flat" });
    impactStar(ink, at0.x + 78, at0.y - 30, impact(phase));
  } else if (u >= FRAMING.square[0] && u < 63.6) {
    builder(ink, { ...KNEEL, armA: 1.5, elbowA: 0.3 }, -2.6, -1.4, { look: 0.6, mouth: "flat" });
  } else {
    builder(ink, idle(u), -2.4, -1.4, { look: 0.6 });
  }
  drawDog(ink, { x: 420, ground: SITE.y + 90, facing: -1, mode: u < 50 ? "sit" : "sleep", t: u });
}

/** In close on a single joist: sighting down it for the crown, and turning it crown up. */
function crownScene(ink: Ink, view: View, u: number): void {
  const p = ink.palette;
  landscape(ink, view, 140);
  const flip = ease.inOut(span(u, FRAMING.flip[0] - FRAMING.crown[0], FRAMING.flip[1] - FRAMING.crown[0]));
  // The bow, exaggerated: down to start with, up once it is turned.
  const bow = mix(-1, 1, flip);
  const x0 = -320;
  const x1 = 300;
  const y = 10;
  const depth = 46 * (0.25 + 0.75 * Math.abs(Math.cos(flip * Math.PI)));
  const top: Pt[] = [];
  const bottom: Pt[] = [];
  for (let i = 0; i <= 12; i += 1) {
    const k = i / 12;
    const x = mix(x0, x1, k);
    const lift = Math.sin(k * Math.PI) * 26 * bow;
    top.push({ x, y: y - depth / 2 - lift });
    bottom.push({ x, y: y + depth / 2 - lift });
  }
  // Two sawhorses under it.
  for (const hx of [-220, 200]) {
    ink.line(hx - 30, 140, hx, y + 26, { width: 5, color: p.woodDark, overshoot: false });
    ink.line(hx + 30, 140, hx, y + 26, { width: 5, color: p.woodDark, overshoot: false });
    ink.rect(hx - 40, y + 20, 80, 10, { fill: p.woodDark, width: 2, offset: 0 });
  }
  ink.shape([...top, ...[...bottom].reverse()], { fill: p.wood, width: 2.6, offset: 1 });
  // The crown, pointed out: under it while it is down, over it once it is up.
  const middle = { x: (x0 + x1) / 2, y: bow < 0 ? bottom[6]!.y : top[6]!.y };
  const crownK = shown(u, 2.4, FRAMING.flip[0] - FRAMING.crown[0] - 0.4);
  label(ink, "Crown", middle, { x: middle.x + 150, y: middle.y + 110 }, crownK);
  label(ink, "Crown up", middle, { x: middle.x + 150, y: middle.y - 130 }, shown(u, FRAMING.flip[1] - FRAMING.crown[0] + 0.2, 14.4));
  tick(ink, middle.x + 280, middle.y - 130, span(u, FRAMING.flip[1] - FRAMING.crown[0] + 0.8, FRAMING.flip[1] - FRAMING.crown[0] + 1.2));
  // The sight line, along the edge.
  const sight = shown(u, 1.2, 6.8);
  if (sight > 0) {
    ink.line(x0 - 60, y - 34, x1 + 40, y - 34, { width: 2, color: p.signal, alpha: sight, overshoot: false, wobble: 0.2 });
  }

  // The builder, an eye down the board; and the dog at the far end, an eye down its tail.
  const turning = flip > 0 && flip < 1;
  drawFigure(ink, turning ? { ...STAND, lean: 0.4, armA: 1.2, armB: 1.0 } : { ...CROUCH, lean: 0.75, nod: 0.2 }, {
    x: x0 - 110,
    ground: 140,
    facing: 1,
    scale: 1.3,
    eyes: turning ? "open" : "closed",
    mouth: "flat",
    look: 1,
    ...(turning ? { reachA: { x: x0 + 30, y: y - 10 }, reachB: { x: x0 + 40, y: y + 10 } } : {}),
  });
  drawDog(ink, { x: x1 + 120, ground: 140, facing: 1, mode: "sit", t: u });
  if (!turning && u > 3.4 && u < 7.4) {
    // One eye shut, sighting down its own tail.
    ink.line(x1 + 150, 76, x1 + 162, 76, { width: 3, overshoot: false });
  }
  if (turning) {
    speedLines(ink, x0 + 60, y - 50, pulse(flip, 0, 1), 3, 50);
  }
}

function deckScene(ink: Ink, view: View, u: number): void {
  const p = ink.palette;
  backyard(ink, view, u);
  const proj = SITE;
  ground(ink, proj, 0);
  gravelBed(ink, proj, 1);
  skids(ink, proj, 1);
  const deck = span(u, ...DECKING.deck);
  // Glue on the joists under each sheet, just before it goes down.
  frame(ink, proj, SKID_BASE, { ...NO_FRAME, rims: 1, joists: 1 });
  for (let i = 0; i < 4; i += 1) {
    const arrived = arrive(deck, i, 4, 1.4);
    const glued = arrive(clamp(deck + 0.12), i, 4, 1.4);
    if (glued <= 0 || arrived >= 1) {
      continue;
    }
    beadsUnder(ink, i, glued);
  }
  frame(ink, proj, SKID_BASE, { ...NO_FRAME, deck, joints: shown(u, DECKING.joints[0], 19.2) });
  label(ink, "End joint on a joist", on(8, SKID_BASE + JOIST + 0.1, 2), { x: 230, y: 250 }, shown(u, DECKING.joints[1], 19.2));

  const [fl, fr, bl, br] = frameCorners(proj, SKID_BASE + 0.09);
  const diag = span(u, ...DECKING.diagonals);
  if (diag > 0) {
    tape(ink, fl, br, span(diag, 0, 0.5), "A");
    tape(ink, fr, bl, span(diag, 0.5, 1), "B");
  }
  verdict(ink, 330, -220, "A = B", true, span(u, DECKING.diagonalsTick, DECKING.diagonalsTick + 0.6));

  // The builder, screwing the sheets down course by course.
  if (u < DECKING.deck[1] + 0.6) {
    const at0 = on(mix(-0.8, W - 2.2, deck), 0, -1.4);
    drawFigure(ink, { ...KNEEL, armA: 1.4, elbowA: 0.4 }, {
      x: at0.x,
      ground: at0.y,
      facing: 1,
      scale: 1.3,
      mouth: "flat",
      hold: (pen, hand, angle) => {
        // A drill.
        const { ctx } = pen;
        ctx.save();
        ctx.translate(hand.x, hand.y);
        ctx.rotate(angle - Math.PI / 2);
        pen.rect(-6, -4, 28, 14, { fill: p.accent, width: 2, offset: 0 });
        pen.line(22, 3, 36, 3, { width: 2.2, overshoot: false });
        pen.rect(-4, 8, 10, 18, { fill: p.accent, width: 2, offset: 0 });
        ctx.restore();
      },
    });
  } else {
    builder(ink, idle(u), -2.4, -1.4, { look: 0.6 });
  }
  drawDog(ink, { x: 420, ground: SITE.y + 90, facing: -1, mode: "sit", t: u });
}

/** A bead of glue along each joist under sheet `i`, drawn on as `k` goes 0 to 1. */
function beadsUnder(ink: Ink, i: number, k: number): void {
  const sheets = [
    { x0: 0, x1: 4, z0: 4, z1: 8 },
    { x0: 4, x1: 12, z0: 4, z1: 8 },
    { x0: 0, x1: 8, z0: 0, z1: 4 },
    { x0: 8, x1: 12, z0: 0, z1: 4 },
  ];
  const sheet = sheets[i]!;
  const top = SKID_BASE + JOIST;
  for (const x of joistLines()) {
    if (x < sheet.x0 - 0.05 || x > sheet.x1 - 0.05) {
      continue;
    }
    const points: Pt[] = [];
    for (let s = 0; s <= 8; s += 1) {
      const z = mix(sheet.z0 + 0.1, sheet.z1 - 0.1, (s / 8) * ease.out(k));
      const c = on(x + 0.08, top + 0.01, z);
      points.push({ x: c.x + (s % 2 === 0 ? -2 : 2), y: c.y });
    }
    ink.curve(points, { width: 3, color: ink.palette.accent, alpha: 0.9 });
  }
}
